import { VariableType } from 'json-to-graphql-query';

import { DocumentIndex } from '@/interfaces/documentIndex';
import { ListResponse } from '@/interfaces/list';
import store from '@/store/store';
import { EntityResource } from './entity.resource';

/**
 * Resource dokumentów z indeksu
 *
 * @export
 * @class DocumentIndexResource
 * @extends {EntityResource<DocumentIndex>}
 */
export class DocumentIndexResource extends EntityResource<DocumentIndex> {
  public getFields = {
    creatorId: true,
    documentId: true,
    stageId: true,
    statusId: true,
    values: true,
    procedureId: true,
    previousStatuses: true,
    data: true,
    decisions: {
      id: true,
      positive: true,
      startDate: true,
      endDate: true,
      data: true,
      type: true,
      dateRanges: true
    },
    systemNumbers: true,
    currentDecision: {
      id: true,
      name: true,
      number: true,
      data: true,
      positive: true,
      documentId: true,
      type: true,
      startDate: true,
      endDate: true,
      dateRanges: true,
      dataDateRanges: true
    },
    procedure: {
      id: true,
      code: true
    },
    structureNode: {
      id: true,
      epuap: true
    }
  };

  public listFields = {
    needFix: true,
    created: true,
    documentId: true,
    procedureId: true,
    procedure: {
      id: true,
      templateId: true,
      name: true
    },
    data: true,
    statusId: true,
    status: { id: true, name: true, color: true },
    module: true,
    checksum: true,
    currentDecision: {
      id: true,
      name: true,
      number: true,
      data: true,
      positive: true,
      documentId: true,
      type: true,
      startDate: true,
      endDate: true,
      dateRanges: true,
      dataDateRanges: true
    },
    attachments: true,
    values: true,
    signedAttachmentId: true
  };

  public listVariables = { filters: 'MongoDBFilters' };

  constructor() {
    super({
      entityName: 'documentIndex'
    });
  }

  public list(filters: any, preventLoader?: boolean): Promise<ListResponse<DocumentIndex>> {
    return super.list(filters, preventLoader).then(response =>
      store
        .dispatch(
          'auth/checkPermissionsSync',
          response.list.map(document => this.colorPermission(document))
        )
        .then(permissions => {
          response.list.forEach(document => {
            const permission = this.colorPermission(document);
            if (!permissions[permission]) {
              return;
            }

            if (document.values && Object.keys(document.values).filter(key => key.startsWith('CRITERION_')).length) {
              const assessmentValues = Object.keys(document.values)
                .filter(key => key.startsWith('CRITERION_'))
                .map(key => document.values[key]);
              if (assessmentValues.some(value => value.passed === 4)) {
                document.listClasses = 'table-danger';
              } else if (assessmentValues.some(value => value.passed === 3)) {
                document.listClasses = 'table-warning';
              } else {
                document.listClasses = 'table-success';
              }
            } else if (document.data?.warnings?.length) {
              document.listClasses = 'table-warning';
            } else if (document.needFix) {
              document.listClasses = 'table-warning';
            }
          });
          return response;
        })
    );
  }

  /**
   * Pobiera statystyki
   *
   * @param {*} options
   * @returns {Promise<any>}
   * @memberof DocumentIndexResource
   */
  public statistics(options: any): Promise<any> {
    const query = this.prepareOptions({
      query: {
        __variables: { options: 'StatisticOptions!' },
        documentIndexStatistics: {
          __args: {
            options: new VariableType('options')
          }
        }
      }
    });
    return this.query({ query, variables: { options } }, 'documentIndexStatistics');
  }

  /**
   * Pobiera raport finansowy
   *
   * @param {*} variables
   * @returns {Promise<any>}
   * @memberof DocumentIndexResource
   */
  public financialReport(variables: any): Promise<any> {
    const query = this.prepareOptions({
      query: {
        __variables: { procedureId: 'Int!', structureIds: '[Int!]' },
        documentIndexFinancialReport: {
          __args: {
            procedureId: new VariableType('procedureId'),
            structureIds: new VariableType('structureIds')
          }
        }
      }
    });
    return this.query({ query, variables }, 'documentIndexFinancialReport');
  }

  /**
   * Zwraca uprawnienia wymagane do pokolorowania dokumentu
   *
   * @private
   * @param {DocumentIndex} document
   * @returns {string}
   * @memberof DocumentIndexResource
   */
  private colorPermission(document: DocumentIndex): string {
    return document.data?.school?.nodeId
      ? 'DOCUMENT.UPDATE:structure_node:' + document.data.school.nodeId
      : 'DOCUMENT.UPDATE';
  }
}

export const documentIndexResource = new DocumentIndexResource();
